/**
 * @module api/cache
 *
 * API functions for managing the server-side media cache.
 * The cache stores downloaded video/audio files (on R2 or local disk)
 * that can be cleaned up to free storage space.
 */

import { api } from "@/api/config.js";

/**
 * Trigger a cache cleanup on the server.
 * This deletes cached video files that have exceeded the configured
 * retention period (default: 7 days) and removes orphan files
 * not tracked in the database.
 *
 * @returns {Promise<Object>} Server response data with cleanup results
 * @throws {Error} If the cleanup request fails, with a message from
 *   the server or a generic fallback message
 */
// export const cleanup = async (maxAgeDays = 1) => {
export const cleanup = async () => {
  try {
    // const response = await api.post("/admin/cleanup", { maxAgeDays });
    const response = await api.post("/admin/cleanup");
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(
      error.response?.data?.message || "Failed to cleanup the cache",
    );
  }
};

/**
 * Get stats about saved episodes (count and total size on R2).
 * Saved episodes are protected from cache cleanup.
 *
 * @returns {Promise<Object>} Server response data with { count, totalSize }
 * @throws {Error} If the request fails
 */
export const getSavedStats = async () => {
  try {
    const response = await api.get("/admin/saved-stats");
    return response.data.data;
  } catch (error) {
    console.error(error);
    throw new Error(
      error.response?.data?.message || "Failed to get saved episodes stats",
    );
  }
};

/**
 * Clear the entire server cache.
 * Unlike cleanup (which respects retention periods), this removes
 * all cached files regardless of age.
 *
 * @returns {Promise<Object>} Server response data with clear results
 * @throws {Error} If the clear request fails, with a message from
 *   the server or a generic fallback message
 */
export const clear = async () => {
  try {
    const response = await api.get("/admin/clear");
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(
      error.response?.data?.message || "Failed to clear the cache",
    );
  }
};

/**
 * Reset the download state for ALL episodes on the server.
 * Clears cached_at, progress, progress_message, and storage fields,
 * allowing every episode to be re-downloaded from scratch.
 * Admin-only endpoint — useful when uploads get stuck or after
 * changing storage backends.
 *
 * @returns {Promise<Object>} Server response data with { count } of reset episodes
 * @throws {Error} If the reset request fails
 */
export const resetAllDownloads = async () => {
  try {
    const response = await api.post("/admin/reset-all-downloads");
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error(
      error.response?.data?.message || "Failed to reset all downloads",
    );
  }
};
